import React, { useEffect, useRef, useState, useCallback } from "react";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";
import PaymentSkeleton from "../skeleton/payment";

const LocationSearch = ({
  onPlaceSelect,
  defaultAddress,
  defaultAddressTitle,
  searchLocationTitle,
}) => {
  const resultsRef = useRef(null);
  const mapsLoaded = useGoogleMaps();
  const [isEditing, setIsEditing] = useState(!defaultAddress);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  // const resultsElement = document.getElementById("google_suggestions");

  // Re-sync state if defaultAddress changes later (e.g., after fetch)
  useEffect(() => {
    if (defaultAddress) {
      setIsEditing(false);
    }
  }, [defaultAddress]);

  const extract = useCallback((place) => {
    const comp = place.addressComponents || [];

    const get = (types) =>
      comp.find((c) => c.types.some((t) => types.includes(t)))?.longText ?? "";

    const streetNumber = get(["street_number"]);
    const route = get(["route"]);
    const neighborhood = get(["neighborhood"]);
    const sublocality1 = get(["sublocality_level_1"]);
    const sublocality = get(["sublocality"]);
    const locality = get(["locality", "administrative_area_level_2"]);
    const admin1 = get(["administrative_area_level_1"]);
    const country = get(["country"]);
    const postalCode = get(["postal_code"]);

    const parts = [streetNumber, route, neighborhood, sublocality1].filter(
      Boolean
    );
    const address =
      parts.length > 0
        ? parts.join(", ")
        : sublocality || place.formattedAddress?.split(",")[0].trim() || "";

    // Add lat/lng from place.location
    const lat = String(place.location?.lat());
    const lng = String(place.location?.lng());

    setSearchValue(address);

    return {
      address,
      country,
      city: locality,
      state: admin1,
      postalCode,
      lat,
      lng,
    };
  }, []);

  useEffect(() => {
    if (!mapsLoaded) return; // ⬅ Wait until Google Maps is ready
    if (!isEditing) return;

    async function init() {
      const resultsElement = resultsRef.current;
      const textInput = document.getElementById("text-input");
      if (!textInput) return;

      let debounceTimer = null;

      textInput.addEventListener("input", () => {
        const query = textInput.value.trim();

        if (query.length <= 3 && resultsElement) {
          resultsElement.innerHTML = "";
          return;
        }

        // debounce to prevent spamming the API
        clearTimeout(debounceTimer);

        debounceTimer = setTimeout(() => {
          findPlaces(query);
        }, 300); // adjust delay as needed
      });
    }

    async function findPlaces(query) {
      if (loading) return; // prevent overlapping calls

      setLoading(true);

      try {
        const { AutocompleteSessionToken, AutocompleteSuggestion } =
          await window.google.maps.importLibrary("places");

        let request = {
          input: query,
          includedPrimaryTypes: [],
          language: "en-US",
          region: "ng",
        };

        request.sessionToken = new AutocompleteSessionToken();

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const resultsElement = resultsRef.current;
        // console.log(resultsElement);
        if (resultsElement) {
          // console.log("true");
          resultsElement.innerHTML = "";
        }

        for (let suggestion of suggestions) {
          if (resultsElement) {
            const placePrediction = suggestion.placePrediction;

            const listItem = document.createElement("li");
            listItem.textContent = placePrediction.text.toString();
            listItem.classList.add("px-4", "cursor-pointer", "hover:bg-gray");

            listItem.addEventListener("click", async () => {
              const place = placePrediction.toPlace();
              await place.fetchFields({
                fields: [
                  "displayName",
                  "formattedAddress",
                  "location",
                  "addressComponents",
                ],
              });

              resultsElement.innerHTML = "";
              onPlaceSelect(extract(place));
            });

            resultsElement.appendChild(listItem);
          }
        }
      } catch (err) {
        console.error("Places search failed:", err);
      } finally {
        setLoading(false);
      }
    }

    init();

    // eslint-disable-next-line
  }, [isEditing, mapsLoaded, extract]);

  // UI: Saved address + Change
  if (!isEditing && defaultAddress) {
    return (
      <div>
        <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
          {defaultAddressTitle ?? "Address"}
        </label>
        <div className="flex items-center justify-between sm:text-sm h-[40px] px-4 py-2 border border-gray-300 w-full text-base bg-[#FFFFFF] dark:bg-[transparent]">
          <span>{defaultAddress}</span>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="text-primary text-sm font-medium hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // UI: Search box (Create OR Edit after Change)
  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
        {searchLocationTitle ?? "Search for a Location"}
      </label>

      <div className="relative">
        <div id="text-input-card">
          <input
            type="search"
            id="text-input"
            autoComplete="off"
            autoFocus="off"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search for a place"
            className="sm:text-sm min-h-[40px] w-full px-4 border border-gray-300 text-base bg-[#FFFFFF] dark:bg-[transparent] focus:ring-2 focus:ring-red-600 focus:border-red-600 focus:outline-none"
          />
        </div>

        <div className="bg-white dark:bg-black">
          {loading && <PaymentSkeleton />}
          <ul
            ref={resultsRef}
            id="google_suggestions"
            className="flex flex-col gap-4 w-full absolute left-0 right-0 bg-white dark:bg-black z-10 shadow shadow-lg"
          />
        </div>
      </div>

      {defaultAddress && isEditing && (
        <div className="flex justify-end mt-1">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="text-sm text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
